//region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Display the list of classifications for the property.
//
//  Copyright:    © 2023 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   18.09.23 Sean Flook                  Initial Revision.
//    002   06.10.23 Sean Flook                  Use colour variables.
//    003   27.10.23 Sean Flook                  Use new dataFormStyle.
//    004   24.11.23 Sean Flook                  Moved Box and Stack to @mui/system and fixed a warning.
//    005   08.12.23 Sean Flook                  Migrated DataGrid to v6.
//    006   05.01.24 Sean Flook                  Changes to sort out warnings and use CSS shortcuts.
//    007   25.01.24 Sean Flook                  Changes required after UX review.
//    008   16.02.24 Sean Flook         ESU16_GP If changing page etc ensure the information and selection controls are cleared.
//    009   20.02.24 Sean Flook         ESU16_GP Undone above change as not required.
//    010   11.03.24 Sean Flook            GLB12 Adjusted height to remove gap.
//    011   18.03.24 Sean Flook            GLB12 Adjusted height to remove overflow.
//    012   18.03.24 Sean Flook       STRFRM3_OS Set the styling for the header row of the data grid.
//    013   22.03.24 Sean Flook            GLB12 Changed to use dataFormStyle so height can be correctly set.
//    014   20.06.24 Sean Flook        IMANN-636 Use the new user rights.
//endregion Version 1.0.0.0
//region Version 1.0.1.0
//    015   31.10.24 Sean Flook       IMANN-1012 Changed height of skeleton control.
//endregion Version 1.0.1.0
//region Version 1.0.5.0
//    016   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//    017   14.03.25 Sean Flook        IMANN-963 Prevent the selection control from displaying if the user cannot edit the data.
//    018   23.04.25 Sean Flook       IMANN-1768 Changes required for selecting properties in property and street forms.
//    019   24.04.25 Sean Flook       IMANN-1760 Prevent multiple renders from occurring.
//endregion Version 1.0.5.0
//region Version 1.0.6.0
//    020   04.07.25 Sean Flook                  Set the new variant parameter for the ADSSelectionControl.
//    021   08.08.25 Sean Flook       IMANN-1960 Moved styling for DataGrid column headers to App.css.
//endregion Version 1.0.6.0
//region Version 1.0.7.0
//    022   13.06.25 Sean Flook       IMANN-1812 Moved controls from @mui/system to @mui/material.
//    023   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    024   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI X to v8.
//    025   27.10.25 Sean Flook       IMANN-2110 Set getRowHeight to Auto in the grid so that text wraps correctly.
//    026   28.10.25 Sean Flook       IMANN-2110 Use formatTableStartDate and formatTableEndDate for date columns.
//    027   17.12.25 Sean Flook       IMANN-2196 Removed the information and selection components as no longer required in this control.
//endregion Version 1.0.7.0
//region Version 1.0.7.2
//    028   01.05.26 Sean Flook       IMANN-2430 Use currentOrganisationSelection when using the list of ids currently selected when deleting.
//endregion Version 1.0.7.2
//
//--------------------------------------------------------------------------------------------------
//endregion header

import React, { useState, useRef, useContext, useEffect, Fragment } from "react";
import PropTypes from "prop-types";

import UserContext from "../context/userContext";
import SelectionContext from "../context/selectionContext";

import {
  Tooltip,
  IconButton,
  Typography,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Skeleton,
  Box,
  Stack,
} from "@mui/material";

import ADSActionButton from "../components/ADSActionButton";

import ConfirmDeleteDialog from "../dialogs/ConfirmDeleteDialog";

import { AddCircleOutlineOutlined as AddCircleIcon } from "@mui/icons-material";

import { adsBlueA, adsLightBlue10 } from "../utils/ADSColours";
import { toolbarStyle, ActionIconStyle, dataFormStyle, tooltipStyle, StyledDataGrid } from "../utils/ADSStyles";
import { useTheme } from "@mui/material/styles";
import { formatTableEndDate, formatTableStartDate } from "../utils/HelperUtils";

PropertyOrganisationListTab.propTypes = {
  data: PropTypes.array,
  errors: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  onOrganisationSelected: PropTypes.func.isRequired,
  onOrganisationDelete: PropTypes.func.isRequired,
  onMultiOrganisationDelete: PropTypes.func.isRequired,
};

function PropertyOrganisationListTab({
  data,
  errors,
  loading,
  onOrganisationSelected,
  onOrganisationDelete,
  onMultiOrganisationDelete,
}) {
  const theme = useTheme();

  const userContext = useContext(UserContext);
  const selectionContext = useContext(SelectionContext);

  const [sortModel, setSortModel] = useState([{ field: "organisation", sort: "asc" }]);
  const [selectionModel, setSelectionModel] = useState({
    type: "include",
    ids: new Set([]),
  });
  const [selectedRow, setSelectedRow] = useState(null);

  const [userCanEdit, setUserCanEdit] = useState(false);

  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);

  const deleteOrganisationId = useRef(null);

  /**
   * Method to display the action buttons for the given row.
   *
   * @param {object} params The grid row parameters.
   * @returns {JSX.Element} The action buttons for the row
   */
  const displayActionButtons = (params) => {
    const onDeleteClick = (event) => {
      event.stopPropagation();
      deleteOrganisationId.current = params.id;
      setOpenDeleteConfirmation(true);
    };

    return (
      selectedRow &&
      params.id === selectedRow &&
      selectionModel &&
      selectionModel.ids.size < 2 && (
        <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
          <ADSActionButton
            id={`delete-organisation-${params.id}`}
            variant="delete"
            inheritBackground
            disabled={!userCanEdit}
            tooltipTitle="Delete organisation"
            tooltipPlacement="bottom"
            onClick={onDeleteClick}
          />
        </Stack>
      )
    );
  };

  /**
   * Array of fields (columns) to be displayed in the data grid.
   */
  const organisationColumns = [
    { field: "id" },
    { field: "uprn" },
    { field: "changeType" },
    { field: "orgKey" },
    {
      field: "organisation",
      headerName: "Organisation",
      flex: 30,
    },
    { field: "legalName" },
    { field: "entryDate" },
    {
      field: "startDate",
      headerName: "Start",
      flex: 10,
      type: "date",
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row, column, apiRef) => new Date(value),
      renderCell: formatTableStartDate,
    },
    {
      field: "endDate",
      headerName: "End",
      flex: 10,
      type: "date",
      align: "right",
      headerAlign: "right",
      valueGetter: (value, row, column, apiRef) => new Date(value),
      renderCell: formatTableEndDate,
    },
    { field: "lastUpdateDate" },
    {
      field: "",
      display: "flex",
      flex: 2,
      sortable: false,
      renderCell: displayActionButtons,
    },
  ];

  /**
   * Event to handle when the mouse enters a row in the data grid.
   *
   * @param {object} event The event object.
   */
  const handleRowMouseEnter = (event) => {
    event.preventDefault();

    setSelectedRow(Number(event.currentTarget.getAttribute("data-id")));
  };

  /**
   * Event to handle when the mouse leaves a row in the data grid.
   */
  const handleRowMouseLeave = () => {
    setSelectedRow(null);
  };

  /**
   * Event to handle when a organisation record is clicked.
   *
   * @param {object} param The grid row parameters.
   * @param {object} event The event object.
   */
  const handleOrganisationClicked = (param, event) => {
    event.stopPropagation();

    if (param && param.field !== "__check__" && param.field !== "") {
      let record = -1;
      for (let i = 0; i < data.length; i++) {
        if (data[i].id === param.id) {
          record = i;
          break;
        }
      }
      if (onOrganisationSelected) onOrganisationSelected(param.id, param.row, record, data.length);
    }
  };

  /**
   * Event to handle when the add organisation button is clicked.
   *
   * @param {object} event The event object.
   */
  const handleAddOrganisationClick = (event) => {
    event.stopPropagation();
    if (onOrganisationSelected) onOrganisationSelected(0, null, null, null);
  };

  /**
   * Event to handle when the selection model for the data grid changes.
   *
   * @param {object} selectionModel The current selection model for the data grid.
   */
  const handleSelectionModelChange = (selectionModel) => {
    if (selectionModel.type === "include" && selectionModel.ids.size > 0 && userCanEdit) {
      selectionContext.onSelectionVariantChange("organisation");
      selectionContext.onSelectionCountChange(selectionModel.ids.size);
      selectionContext.onCurrentOrganisationChange([...selectionModel.ids]);
    } else if (selectionModel.type === "exclude" && selectionModel.ids.size === 0 && userCanEdit) {
      selectionContext.onSelectionVariantChange("organisation");
      selectionContext.onSelectionCountChange(data.length);
      const allIds = data.map((x) => x.id);
      selectionContext.onCurrentOrganisationChange(allIds);
    } else if (selectionModel.type === "exclude" && selectionModel.ids.size < data.length && userCanEdit) {
      selectionContext.onSelectionVariantChange("organisation");
      selectionContext.onSelectionCountChange(data.length - selectionModel.ids.size);
      const allIds = data.filter((x) => !selectionModel.ids.has(x.id)).map((x) => x.id);
      selectionContext.onCurrentOrganisationChange(allIds);
    } else {
      selectionContext.onCloseSelection();
    }
  };

  /**
   * Event to handle when the delete confirmation dialog is closed.
   *
   * @param {boolean} deleteConfirmed True if the user has confirmed the delete; otherwise false.
   */
  const handleCloseDeleteConfirmation = (deleteConfirmed) => {
    setOpenDeleteConfirmation(false);
    if (deleteConfirmed) {
      if (selectionContext.currentOrganisationSelection?.length > 0 && onMultiOrganisationDelete) {
        onMultiOrganisationDelete(selectionContext.currentOrganisationSelection);
        selectionContext.onCloseSelection();
        setSelectionModel({
          type: "include",
          ids: new Set([]),
        });
      } else if (deleteOrganisationId.current && onOrganisationDelete) {
        onOrganisationDelete(deleteOrganisationId.current);
        deleteOrganisationId.current = null;
      }
    }
  };

  /**
   * Method to determine if the row is valid or not.
   *
   * @param {number} id The id of the row.
   * @returns {boolean} True if the row is valid; otherwise false.
   */
  const isRowInvalid = (id) => {
    if (errors && errors.length > 0) {
      const rowErrors = errors.filter((x) => x.id === id);
      return rowErrors && rowErrors.length > 0;
    } else return false;
  };

  useEffect(() => {
    if (userCanEdit !== (userContext.currentUser && userContext.currentUser.editProperty))
      setUserCanEdit(userContext.currentUser && userContext.currentUser.editProperty);
  }, [userCanEdit, userContext.currentUser]);

  useEffect(() => {
    if (selectionContext.closeControl) {
      selectionContext.onControlClosed();
      setSelectionModel({
        type: "include",
        ids: new Set([]),
      });
    }
  }, [selectionContext, selectionContext.closeControl]);

  useEffect(() => {
    if (selectionContext.deleteOrganisation && selectionContext.currentOrganisationSelection?.length > 0) {
      selectionContext.onDeleteOrganisation(false);
      setOpenDeleteConfirmation(true);
    }
  }, [selectionContext, selectionModel]);

  return (
    <Fragment>
      <Box sx={toolbarStyle} id="ads-property-organisation-list-tab">
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1" sx={{ pl: theme.spacing(2) }}>
            Organisations
          </Typography>
          <Tooltip title="Add new organisation record" arrow placement="right" sx={tooltipStyle}>
            <IconButton
              sx={ActionIconStyle()}
              disabled={!userCanEdit}
              onClick={handleAddOrganisationClick}
              size="small"
            >
              <AddCircleIcon />
              <Typography
                variant="subtitle1"
                sx={{
                  pl: theme.spacing(1),
                  pr: theme.spacing(1),
                }}
              >
                Organisation
              </Typography>
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Box
        sx={{
          ...dataFormStyle("PropertyOrganisationListTab"),
        }}
      >
        {loading ? (
          <Skeleton variant="rectangular" height="30px" width="100%" />
        ) : data && data.filter((x) => x.changeType !== "D").length > 0 ? (
          <StyledDataGrid
            rows={data.filter((x) => x.changeType !== "D")}
            columns={organisationColumns}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  id: false,
                  changeType: false,
                  uprn: false,
                  orgKey: false,
                  legalName: false,
                  entryDate: false,
                  lastUpdateDate: false,
                },
              },
            }}
            autoPageSize
            checkboxSelection
            disableColumnMenu
            pagination
            sortModel={sortModel}
            rowSelectionModel={selectionModel}
            slotProps={{
              row: {
                onMouseEnter: handleRowMouseEnter,
                onMouseLeave: handleRowMouseLeave,
              },
            }}
            getRowClassName={(params) => `${isRowInvalid(params.id) ? "invalid-row" : "valid-row"}`}
            getRowHeight={(params) => "auto"}
            onCellClick={handleOrganisationClicked}
            onRowSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
              handleSelectionModelChange(newSelectionModel);
            }}
            onSortModelChange={(model) => setSortModel(model)}
          />
        ) : (
          <List
            sx={{
              width: "100%",
              backgroundColor: theme.palette.background.paper,
              pt: theme.spacing(0),
            }}
            component="nav"
            key="key_no_records"
          >
            <ListItemButton
              dense
              disabled={!userCanEdit}
              disableGutters
              onClick={handleAddOrganisationClick}
              sx={{
                height: "30px",
                "&:hover": {
                  backgroundColor: adsLightBlue10,
                  color: adsBlueA,
                },
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="subtitle1" sx={{ pl: "16px" }}>
                    No organisation records present
                  </Typography>
                }
              />
              <ListItemAvatar
                sx={{
                  minWidth: 32,
                }}
              >
                <Tooltip title="Add organisation record" arrow placement="bottom" sx={tooltipStyle}>
                  <IconButton disabled={!userCanEdit} onClick={handleAddOrganisationClick} size="small">
                    <AddCircleIcon sx={ActionIconStyle()} />
                  </IconButton>
                </Tooltip>
              </ListItemAvatar>
            </ListItemButton>
          </List>
        )}
      </Box>
      <div>
        <ConfirmDeleteDialog
          variant="organisation"
          recordCount={selectionModel && selectionModel.ids.size > 0 ? selectionModel.ids.size : 0}
          open={openDeleteConfirmation}
          onClose={handleCloseDeleteConfirmation}
        />
      </div>
    </Fragment>
  );
}

export default PropertyOrganisationListTab;
